<template>
  <v-container fluid class="pos-container">
    <!-- BUSCADOR -->
    <v-row class="buscador-row">
      <v-col>
        <producto-buscador-multiple
          @loading-buscar-producto="setLoadingBuscador"
          ref="search"
          @focus.native="$event.target.select()"
          @seleccionar-producto-local="getProductoConCodigoLocal"
          @resultado-buscar-producto="setBuscarProducto"
          @clean-buscador="cleanBuscador"
          :showSuggestions="showSuggestions"
          :endpoint="endpointBuscador"
        />
      </v-col>
    </v-row>

    <!-- CONTENIDO -->
    <v-row class="contenido-row">
      <!-- CATEGORÍAS -->
      <v-col cols="2" class="categorias-col">
        <div class="categorias-wrapper">
          <v-btn icon small @click="scrollUp" :disabled="!puedeSubir">
            <v-icon>mdi-chevron-up</v-icon>
          </v-btn>

          <div ref="lista" class="categorias-scroll" @scroll="updateScroll">
            <v-card
              v-for="cat in categorias"
              :key="cat.id"
              class="mb-2 d-flex flex-column align-center justify-center"
              :elevation="categoriaSeleccionadaId === cat.id ? 6 : 2"
              height="90"
              @click="selectCategoria(cat)"
            >
              <v-icon>{{ cat.icono || "mdi-folder" }}</v-icon>
              <span class="text-caption text-center">
                {{ cat.name }}
              </span>
            </v-card>
          </div>

          <v-btn icon small @click="scrollDown" :disabled="!puedeBajar">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </div>
      </v-col>

      <!-- PRODUCTOS -->
      <v-col cols="10">
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="primary"
          class="mb-4"
        />

        <v-row v-else>
          <v-col
            v-for="(producto, index) in productos"
            :key="producto.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card
              :ref="'producto-' + index"
              class="producto-card d-flex flex-column justify-space-between"
              :class="{ 'producto-activo': index === selectedIndex }"
              :elevation="index === selectedIndex ? 12 : 4"
              :disabled="sinStock(producto)"
              @click="addProducto(producto)"
              hover
            >
              <v-card-text
                class="d-flex flex-column justify-center align-center text-center flex-grow-1"
              >
                <div class="text-subtitle-1 font-weight-medium mb-2">
                  {{ producto.descripcion }}
                </div>
                <div class="text-h5 font-weight-bold">
                  $ {{ producto[priceField] }}
                </div>

                <v-chip
                  v-if="sinStock(producto)"
                  color="error"
                  small
                  class="mt-2"
                >
                  Sin stock
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import CategoriasAPI from "@/api/categorias";
import { mapState } from "vuex";
import ProductoBuscadorMultiple from "@/components/producto/buscador/producto.buscador.multiple";

export default {
  name: "ProductoSelector",

  components: {
    ProductoBuscadorMultiple,
  },

  props: {
    priceField: {
      type: String,
      default: "precio_venta",
    },
    showSuggestions: {
      type: Boolean,
      default: true,
    },
    isCompra: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      selectedIndex: 0,

      puedeSubir: false,
      puedeBajar: false,
      loading: false,

      categoriaSeleccionadaId: null,
      categoriaSeleccionada: null,

      categorias: [],
      productos: [],

      paginacion: {
        page: 1,
        rowsPerPage: 50,
      },
    };
  },

  computed: {
    ...mapState("sucursales", ["configuracion"]),

    inventario() {
      return this.configuracion?.productoInventario;
    },

    endpointBuscador() {
      const filtro = this.isCompra ? 'es_comprable=true' : 'es_vendible=true';
      return `/productos/psp?${filtro}`;
    },
  },

  watch: {
    productos() {
      this.selectedIndex = 0;
      this.$nextTick(() => {
        this.scrollToSelected();
      });
    },
  },

  methods: {
    setLoadingBuscador(bool) {
      this.loading = bool;
    },

    cleanBuscador() {
      this.productos = [];
      this.selectedIndex = 0;
    },

    getProductoConCodigoLocal() {
      if (this.productos.length === 1) {
        this.addProducto(this.productos[0]);
      }
    },

    setBuscarProducto(data) {
      if (!data) return;
      this.productos = data;
    },

    sinStock(producto) {
      // Si es una compra, nunca bloqueamos por falta de stock
      if (this.isCompra) return false;

      return (
        this.inventario && producto.controlaStock && producto.cantidadstock <= 0
      );
    },

    fetchCategoriasPadre() {
      const uso = this.isCompra ? 'compra' : null;
      CategoriasAPI.index(null, null, false, uso).then((response) => {
        this.categorias = response.data.parent.children;
      });
    },

    addProducto(producto) {
      this.$emit("process-producto", producto);
    },

    selectCategoria(cat) {
      this.loading = true;
      this.categoriaSeleccionada = cat;
      this.categoriaSeleccionadaId = cat.id;

      const filtroContexto = this.isCompra ? 'es_comprable=true' : 'es_vendible=true';
      const url = `/productos/psp?${filtroContexto}&q=&page=${this.paginacion.page}&page_size=${this.paginacion.rowsPerPage}&categoria=${cat.name}`;

      this.axios.get(url).then((res) => {
        this.productos = res.data.results.map(p => ({
          ...p,
          precioVenta: p.precio_venta ?? p.precioVenta,
          precioCosto: p.precio_costo ?? p.precioCosto,
          cantidadstock: p.cantidad_stock ?? p.cantidadstock,
          controlaStock: p.controla_stock ?? p.controlaStock,
        }));
        this.loading = false;
      });
    },

    handleKeydown(e) {
      if (!this.productos.length) return;

      const columns = this.getColumnCount();

      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          if (this.selectedIndex < this.productos.length - 1)
            this.selectedIndex++;
          break;

        case "ArrowLeft":
          e.preventDefault();
          if (this.selectedIndex > 0) this.selectedIndex--;
          break;

        case "ArrowDown":
          e.preventDefault();
          if (this.selectedIndex + columns < this.productos.length)
            this.selectedIndex += columns;
          break;

        case "ArrowUp":
          e.preventDefault();
          if (this.selectedIndex - columns >= 0) this.selectedIndex -= columns;
          break;

        case "Enter":
          e.preventDefault();
          const producto = this.productos[this.selectedIndex];
          // Si es compra, sinStock(producto) será false, permitiendo addProducto
          if (producto && !this.sinStock(producto)) {
            this.addProducto(producto);
          }
          break;
      }

      this.$nextTick(() => {
        this.scrollToSelected();
      });
    },

    getColumnCount() {
      if (this.$vuetify.breakpoint.lg) return 4;
      if (this.$vuetify.breakpoint.md) return 3;
      if (this.$vuetify.breakpoint.sm) return 2;
      return 1;
    },

    scrollToSelected() {
      const ref = this.$refs["producto-" + this.selectedIndex];
      if (ref && ref[0]) {
        ref[0].$el.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    },

    updateScroll() {
      const el = this.$refs.lista;
      if (!el) return;

      this.puedeSubir = el.scrollTop > 0;
      this.puedeBajar = el.scrollTop + el.clientHeight < el.scrollHeight;
    },

    scrollUp() {
      this.$refs.lista.scrollBy({ top: -120, behavior: "smooth" });
      this.$nextTick(this.updateScroll);
    },

    scrollDown() {
      this.$refs.lista.scrollBy({ top: 120, behavior: "smooth" });
      this.$nextTick(this.updateScroll);
    },
  },

  mounted() {
    window.addEventListener("keydown", this.handleKeydown);
    this.updateScroll();
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.handleKeydown);
  },

  created() {
    this.fetchCategoriasPadre();
  },
};
</script>

<style scoped>
.pos-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.buscador-row {
  flex: 0 0 auto;
}

.contenido-row {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.categorias-col {
  display: flex;
  flex-direction: column;
}

.categorias-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.categorias-scroll {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.producto-card {
  border-radius: 14px;
  transition: all 0.18s ease;
  height: 190px;
}

.producto-card:hover:not(.v-card--disabled) {
  transform: translateY(-4px);
}

.producto-card.v-card--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.producto-activo {
  border: 2px solid #1976d2;
  transform: scale(1.02);
}
</style>